import { Fragment, type CSSProperties, type ReactNode } from "react";

import { sx } from "../style";

export type AlertTone = "info" | "success" | "warning" | "error";

const tones: Record<AlertTone, [string, string, string]> = {
  info: ["var(--color-status-info-bg)", "var(--color-status-info-border)", "var(--color-status-info)"],
  success: ["var(--color-status-success-bg)", "var(--color-status-success-border)", "var(--color-status-success)"],
  warning: ["var(--color-status-warning-bg)", "var(--color-status-warning-border)", "var(--color-status-warning)"],
  error: ["var(--color-status-error-bg)", "var(--color-status-error-border)", "var(--color-status-error)"],
};
const glyph: Record<AlertTone, string> = {
  info: "M12 8h.01M11 12h1v4h1",
  success: "M5 13l4 4L19 7",
  warning: "M12 9v4m0 4h.01",
  error: "M15 9l-6 6m0-6l6 6",
};

const CloseX = ({ onClose, size }: { onClose: () => void; size: number }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Закрити"
    style={{ border: "none", background: "transparent", color: "var(--color-text-tertiary)", cursor: "pointer", flex: "none", padding: 2 }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  </button>
);

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
  action?: ReactNode;
  style?: CSSProperties;
}

/** Inline contextual message banner. */
export function Alert({ tone = "info", title, children, onClose, action, style }: AlertProps) {
  const [bg, border, fg] = tones[tone] || tones.info;
  return (
    <div
      role="alert"
      style={sx({ display: "flex", gap: 12, padding: "12px 14px", background: bg, border: `1px solid ${border}`, borderRadius: "var(--radius-md)", ...style })}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={fg} strokeWidth="2" style={{ flex: "none", marginTop: 1 }}>
        {tone === "warning" ? (
          <Fragment>
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <path d={glyph[tone]} strokeLinecap="round" />
          </Fragment>
        ) : (
          <Fragment>
            <circle cx="12" cy="12" r="9" />
            <path d={glyph[tone]} strokeLinecap="round" strokeLinejoin="round" />
          </Fragment>
        )}
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={sx({ fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-body)", color: "var(--color-text-primary)", marginBottom: children ? 2 : 0 })}>
            {title}
          </div>
        )}
        {children && <div style={sx({ fontSize: "var(--fs-body-sm)", color: "var(--color-text-secondary)" })}>{children}</div>}
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
      {onClose && <CloseX onClose={onClose} size={16} />}
    </div>
  );
}

export interface ToastProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
  style?: CSSProperties;
}

/** Transient toast notification (render inside a fixed stack). */
export function Toast({ tone = "info", title, children, onClose, style }: ToastProps) {
  const [, , fg] = tones[tone] || tones.info;
  return (
    <div
      role="status"
      style={sx({
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        width: 340,
        padding: "12px 14px",
        background: "var(--neutral-0)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-popover)",
        borderLeft: `3px solid ${fg}`,
        animation: "cf-slide-up var(--duration-base) var(--ease-out)",
        ...style,
      })}
    >
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: fg, marginTop: 6, flex: "none" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={sx({ fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-body-sm)" })}>{title}</div>}
        {children && (
          <div style={sx({ fontSize: "var(--fs-body-sm)", color: "var(--color-text-secondary)", marginTop: 1 })}>{children}</div>
        )}
      </div>
      {onClose && <CloseX onClose={onClose} size={14} />}
    </div>
  );
}
